// Ledger period selection uses a drawer calendar, keeping date entry free of mobile keyboards.
import { useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { AppDrawer } from '@/components/ui/AppDrawer';
import { Button } from '@/components/ui/Button';
import { ledgerMonthRange, ledgerTodayRange } from '@/lib/ledgerFilters';
import { cn } from '@/lib/utils';

interface LedgerDatePickerProps {
  selectedMonth: string;
  selectedDay: string;
  fallbackLabel: string;
  en: boolean;
  onSelect: (range: { from: string; to: string }) => void;
}

export function LedgerDatePicker({ selectedMonth, selectedDay, fallbackLabel, en, onSelect }: LedgerDatePickerProps) {
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'month' | 'day'>('month');
  const [month, setMonth] = useState(1);
  const [year, setYear] = useState(new Date().getFullYear());
  const monthValue = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
  const range = ledgerMonthRange(monthValue)!;
  const firstWeekday = (new Date(`${range.from}T12:00:00Z`).getUTCDay() + 6) % 7;
  const days = Number(range.to.slice(-2));
  const currentYear = new Date().getFullYear();
  const years = Array.from(new Set([...Array.from({ length: 21 }, (_, index) => currentYear - 10 + index), year]))
    .filter(value => value >= 1 && value <= 9999).sort((a, b) => a - b);
  const monthName = (number: number, long = false) => new Intl.DateTimeFormat(en ? 'en-AU' : 'zh-CN', {
    month: long ? 'long' : 'short', timeZone: 'UTC',
  }).format(new Date(`2000-${String(number).padStart(2, '0')}-01T12:00:00Z`));
  const selectedLabel = selectedDay || (selectedMonth ? new Intl.DateTimeFormat(en ? 'en-AU' : 'zh-CN', {
    year: 'numeric', month: 'long', timeZone: 'UTC',
  }).format(new Date(`${selectedMonth}-01T12:00:00Z`)) : fallbackLabel);

  function changeOpen(next: boolean) {
    if (next) {
      const candidate = selectedDay || selectedMonth;
      const initial = ledgerMonthRange(candidate.slice(0, 7)) ? candidate : ledgerTodayRange().from;
      setYear(Number(initial.slice(0, 4)));
      setMonth(Number(initial.slice(5, 7)));
      setMode(selectedDay ? 'day' : 'month');
    }
    setOpen(next);
    if (!next) requestAnimationFrame(() => trigger.current?.focus({ preventScroll: true }));
  }

  function select(from: string, to: string) {
    onSelect({ from, to });
    changeOpen(false);
  }

  const optionClass = 'min-h-11 min-w-0 rounded-xl border text-sm font-semibold transition-colors';
  const selectedClass = 'border-primary bg-primary text-primary-foreground';
  const unselectedClass = 'border-border bg-card hover:bg-muted';

  return <>
    <button
      ref={trigger}
      type="button"
      aria-label={selectedDay ? (en ? 'Choose ledger date' : '选择账本日期') : (en ? 'Choose ledger month' : '选择账本月份')}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-value={selectedDay || selectedMonth}
      onClick={() => changeOpen(true)}
      className="flex min-h-11 w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-border bg-muted/60 px-3 py-2 text-left text-sm font-semibold text-foreground hover:bg-muted"
    >
      <span>{selectedLabel}</span><CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
    </button>
    <AppDrawer open={open} onOpenChange={changeOpen} title={en ? 'Choose ledger period' : '选择账本时间'}>
      <div className="mx-auto max-w-md space-y-3 pb-2">
        <div className="grid grid-cols-2 gap-2" aria-label={en ? 'Selection type' : '时间选择方式'}>
          {(['month', 'day'] as const).map(value => <button key={value} type="button" aria-pressed={mode === value}
            className={cn(optionClass, mode === value ? selectedClass : unselectedClass)} onClick={() => setMode(value)}>
            {value === 'month' ? (en ? 'Month' : '月份') : (en ? 'Date' : '日期')}
          </button>)}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" className="h-11 w-11 shrink-0 p-0" aria-label={en ? 'Previous year' : '上一年'} disabled={year === 1} onClick={() => setYear(value => value - 1)}><ChevronLeft className="h-4 w-4" aria-hidden /></Button>
          <select aria-label={en ? 'Choose year' : '选择年份'} value={year} onChange={event => setYear(Number(event.target.value))} className="h-11 min-w-0 flex-1 rounded-xl border bg-card px-2 text-base">
            {years.map(value => <option key={value} value={value}>{value}{en ? '' : '年'}</option>)}
          </select>
          {mode === 'day' && <select aria-label={en ? 'Choose month' : '选择月份'} value={month} onChange={event => setMonth(Number(event.target.value))} className="h-11 min-w-0 flex-1 rounded-xl border bg-card px-2 text-base">
            {Array.from({ length: 12 }, (_, index) => <option key={index} value={index + 1}>{monthName(index + 1)}</option>)}
          </select>}
          <Button variant="secondary" className="h-11 w-11 shrink-0 p-0" aria-label={en ? 'Next year' : '下一年'} disabled={year === 9999} onClick={() => setYear(value => value + 1)}><ChevronRight className="h-4 w-4" aria-hidden /></Button>
        </div>
        {mode === 'month' ? <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 12 }, (_, index) => {
            const value = `${String(year).padStart(4, '0')}-${String(index + 1).padStart(2, '0')}`;
            return <button key={value} type="button" aria-label={value} aria-pressed={selectedMonth === value}
              className={cn(optionClass, selectedMonth === value ? selectedClass : unselectedClass)}
              onClick={() => { const chosen = ledgerMonthRange(value)!; select(chosen.from, chosen.to); }}>{monthName(index + 1, true)}</button>;
          })}
        </div> : <div className="grid grid-cols-7 gap-1">
          {(en ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] : ['一', '二', '三', '四', '五', '六', '日']).map(label => <span key={label} className="py-1 text-center text-xs text-muted-foreground">{label}</span>)}
          {Array.from({ length: firstWeekday }, (_, index) => <span key={`empty-${index}`} aria-hidden />)}
          {Array.from({ length: days }, (_, index) => {
            const value = `${monthValue}-${String(index + 1).padStart(2, '0')}`;
            return <button key={value} type="button" aria-label={value} aria-pressed={selectedDay === value}
              className={cn(optionClass, selectedDay === value ? selectedClass : unselectedClass)} onClick={() => select(value, value)}>{index + 1}</button>;
          })}
        </div>}
        <Button variant="secondary" className="w-full" onClick={() => changeOpen(false)}>{en ? 'Cancel' : '取消'}</Button>
      </div>
    </AppDrawer>
  </>;
}
